import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { cn } from '@/lib/utils/cn';
import {
  CalendarClock,
  ListChecks,
  Loader2,
  Settings2,
  Pencil,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useUserDetails } from '@/features/users/api';
import {
  useCreateReportSchedule,
  useReportParameters,
  useReportSchedules,
  useUpdateReportSchedule,
} from '../api';
import type {
  ReportScheduleRow,
  ScheduleFrequency,
} from '../types';

const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

interface EditState {
  id: number;
  frequency: ScheduleFrequency;
  parameters: string[];
  enabled: boolean;
}

/** Parse the legacy parameterJson string ("p1,p2,p3" or '"p1","p2"'). */
function parseParameterJson(raw: string | undefined | null): string[] {
  if (!raw) return [];
  return raw
    .replace(/"/g, '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function ReportSchedulingPage() {
  const userId = useAuthStore((s) => s.userId);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const canView = usePermission('Manage Campaign.Report Scheduling', BASE_PERMISSION.VIEW);
  const navigate = useNavigate();

  const profile = useUserDetails(userId);
  const schedules = useReportSchedules();
  const params = useReportParameters();
  const create = useCreateReportSchedule();
  const update = useUpdateReportSchedule();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [frequency, setFrequency] = useState<ScheduleFrequency | ''>('');
  const [selected, setSelected] = useState<string[]>([]);
  const [enabled, setEnabled] = useState(true);

  const rows = schedules.data ?? [];

  // Lazy-load parameters the first time the dialog opens.
  useEffect(() => {
    if (open && params.data === undefined && !params.isFetching) {
      params.refetch();
    }
  }, [open, params]);

  const allSelected =
    (params.data?.length ?? 0) > 0 && selected.length === (params.data?.length ?? 0);
  const partiallySelected = selected.length > 0 && !allSelected;

  const resetForm = () => {
    setEditing(null);
    setFrequency('');
    setSelected([]);
    setEnabled(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpen(true);
  };

  const handleOpenEdit = (row: ReportScheduleRow) => {
    setEditing({
      id: row.id,
      frequency: row.frequency,
      parameters: parseParameterJson(row.parameterJson),
      enabled: row.enabled ?? false,
    });
    setFrequency(row.frequency);
    setSelected(parseParameterJson(row.parameterJson));
    setEnabled(row.enabled ?? false);
    setOpen(true);
  };

  const toggleParam = (name: string) => {
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name],
    );
  };

  const handleSubmit = () => {
    if (selected.length === 0) {
      toast.error('Pick at least one report parameter.');
      return;
    }
    if (!frequency) {
      toast.error('Pick a frequency.');
      return;
    }
    const email = profile.data?.emailid ?? '';
    if (!email) {
      toast.error('Your profile is missing an email address.');
      return;
    }

    const parameterJson = selected.join(',');

    if (editing) {
      update.mutate(
        {
          id: editing.id,
          userId: Number(userId),
          email,
          frequency,
          enabled,
          status: 'IDLE',
          timezone: 'Asia/Kolkata',
          parameterJson,
        },
        {
          onSuccess: (res) => {
            toast.success(res?.message ?? 'Schedule updated');
            handleClose();
          },
          onError: () => toast.error('Could not update the schedule.'),
        },
      );
    } else {
      create.mutate(
        {
          userId: Number(userId),
          email,
          frequency,
          enable: true,
          status: 'IDLE',
          timezone: 'Asia/Kolkata',
          parameterJson,
        },
        {
          onSuccess: (res) => {
            toast.success(res?.message ?? 'Schedule created');
            handleClose();
          },
          onError: () => toast.error('Could not create the schedule.'),
        },
      );
    }
  };

  const columns = useMemo<ColumnDef<ReportScheduleRow>[]>(() => {
    const cols: ColumnDef<ReportScheduleRow>[] = [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      { accessorKey: 'frequency', header: 'Frequency', size: 110 },
    ];

    if (isSubOrSuper) {
      cols.push(
        { accessorKey: 'userId', header: 'User ID', size: 100 },
        { accessorKey: 'username', header: 'Username', size: 160 },
      );
    }

    cols.push(
      {
        accessorKey: 'enabled',
        header: 'Enabled',
        size: 100,
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={cn(
              'font-normal',
              row.original.enabled
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-muted-foreground/20 bg-muted text-muted-foreground',
            )}
          >
            {row.original.enabled ? 'Yes' : 'No'}
          </Badge>
        ),
      },
      { accessorKey: 'status', header: 'Status', size: 110 },
      { accessorKey: 'timezone', header: 'Timezone', size: 140 },
      {
        accessorKey: 'parameterJson',
        header: 'Parameters',
        size: 260,
        cell: ({ row }) => {
          const parts = parseParameterJson(row.original.parameterJson);
          return (
            <span className="text-xs text-muted-foreground">
              {parts.length === 0 ? '—' : parts.join(', ')}
            </span>
          );
        },
      },
      { accessorKey: 'createdAt', header: 'Created', size: 180 },
      { accessorKey: 'updatedAt', header: 'Updated', size: 180 },
      { accessorKey: 'nextRunAt', header: 'Next run', size: 180 },
      {
        id: 'actions',
        header: 'Settings',
        size: 100,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Schedule #{row.original.id}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate(`/report-scheduling-logs/${row.original.id}`)}
              >
                <ListChecks className="h-4 w-4" />
                View logs
              </DropdownMenuItem>
              {!isSubOrSuper && (
                <DropdownMenuItem onClick={() => handleOpenEdit(row.original)}>
                  <Pencil className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    );

    return cols;
  }, [isSubOrSuper, navigate]);

  const isPending = create.isPending || update.isPending;

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Scheduled Reports"
        description="Schedule periodic CSV report emails to be delivered automatically."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => schedules.refetch()}
              disabled={schedules.isFetching}
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw className={cn('h-4 w-4', schedules.isFetching && 'animate-spin')} />
            </Button>
            {!isSubOrSuper && (
              <Button onClick={handleOpenCreate}>
                <Plus className="h-4 w-4" />
                New schedule
              </Button>
            )}
          </div>
        }
      />

      <DataTable<ReportScheduleRow, unknown>
        tableId="reports:schedule:v1"
        columns={columns}
        data={rows}
        isLoading={schedules.isLoading}
        searchPlaceholder="Search schedules…"
        emptyTitle="No schedules yet"
        emptyDescription="Create a schedule to start receiving periodic reports by email."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="report-schedules.csv"
        pageSize={10}
      />

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : handleClose())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edit schedule' : 'New report schedule'}
            </DialogTitle>
            <DialogDescription>
              Pick the columns you want in the CSV and how often you want it delivered.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Report parameters</Label>
              <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border border-border p-2">
                <label className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-muted">
                  <Checkbox
                    checked={
                      allSelected ? true : partiallySelected ? 'indeterminate' : false
                    }
                    onCheckedChange={(v) => {
                      if (v) setSelected((params.data ?? []).map((p) => p.paramName));
                      else setSelected([]);
                    }}
                  />
                  <span className="text-sm font-medium">Select all</span>
                </label>
                <div className="border-t border-border" />
                {params.isLoading ? (
                  <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading parameters…
                  </div>
                ) : (params.data ?? []).length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground">
                    No parameters available.
                  </div>
                ) : (
                  (params.data ?? []).map((p) => (
                    <label
                      key={p.paramName}
                      className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-muted"
                    >
                      <Checkbox
                        checked={selected.includes(p.paramName)}
                        onCheckedChange={() => toggleParam(p.paramName)}
                      />
                      <span className="text-sm">{p.paramHeading}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="frequency">Frequency</Label>
                <Select
                  value={frequency}
                  onValueChange={(v) => setFrequency(v as ScheduleFrequency)}
                >
                  <SelectTrigger id="frequency">
                    <SelectValue placeholder="Pick a frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editing && (
                <div className="flex items-end">
                  <label className="flex cursor-pointer items-center gap-2 py-2">
                    <Checkbox
                      checked={enabled}
                      onCheckedChange={(v) => setEnabled(Boolean(v))}
                    />
                    <span className="text-sm">Enabled</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CalendarClock className="h-4 w-4" />
              )}
              {editing ? 'Save changes' : 'Create schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </FillPage>
  );
}
